import { Activity, SendHorizonal, Square } from 'lucide-react'
import { useState } from 'react'

import { AutoResizeTextarea, Button } from '@/components/ui'
import { useToast } from '@/hooks'
import {
  useAccountLoaders,
  useChatUsage,
  useIsTrialAccount,
  useSummary,
  useTrialChatUsedUp,
} from '@/modules/account'
import { ChatCreditsExhaustedError } from '@/modules/errors'
import { cn, fromNow } from '@/utils'
import {
  useChatMutations,
  useChatSending,
  useCurrentSessionId,
  useDiagnosticsEnabled,
} from '../hooks'
import { IS_ADMIN_MODE } from '../hooks/useIsAdminMode'
import { TrialChatUsedUpNote } from './TrialChatUsedUpNote'

interface ChatComposerProps {
  slug: string
}

// Per-message character cap, enforced at two places — both must move
// together:
//
//   1. militao ChatComposer.MAX_MESSAGE_LENGTH   (this constant, applied
//      to the textarea's maxLength attribute below)
//   2. daystrom routes.MAX_MESSAGE_LENGTH        (Pydantic validator on
//      the /chat request body)
//
// Lukita's chat_messages table is a plain text column with no length
// validation, so it does not need to be touched — and it offers no
// backstop if either of the two enforcers above is loosened.
const MAX_MESSAGE_LENGTH = 2000
// Show the counter once the user is within this many chars of the cap.
const COUNTER_THRESHOLD = 200

export default function ChatComposer({ slug }: ChatComposerProps) {
  const [value, setValue] = useState('')
  const sending = useChatSending()
  const sessionId = useCurrentSessionId()
  const diagnosticsEnabled = useDiagnosticsEnabled()
  const { sendMessage, stopMessage, setDiagnosticsEnabled } = useChatMutations()
  const { errorToast, toast } = useToast()
  const chatUsage = useChatUsage()
  const summary = useSummary()
  const isTrial = useIsTrialAccount()
  // Shows the note at once on a refused send, before the refreshed summary arrives.
  const [wasRefusedForTrial, setWasRefusedForTrial] = useState(false)
  const trialChatUsedUp = useTrialChatUsedUp() || wasRefusedForTrial
  const { fetchAccountSummary } = useAccountLoaders()

  // `chatUsage` already reflects whether enforcement is active (the store
  // empties it when chat is unlimited — see CHAT_ENFORCEMENT_ENABLED), so the
  // composer can read `exceeded` directly without re-checking the flag.
  const disabled = sessionId === null || chatUsage.exceeded
  const charCount = value.length
  const remaining = MAX_MESSAGE_LENGTH - charCount
  const showCounter = remaining <= COUNTER_THRESHOLD
  const overLimit = remaining < 0

  function handleSend() {
    const trimmed = value.trim()
    if (!trimmed || disabled || sending || overLimit) return
    sendMessage(slug, trimmed).catch((error) => {
      if (error instanceof ChatCreditsExhaustedError && isTrial) {
        setValue(trimmed)
        setWasRefusedForTrial(true)
        void fetchAccountSummary()
        return
      }
      if (error instanceof ChatCreditsExhaustedError) {
        toast({
          title: 'Monthly chat allowance reached',
          description: `Resets ${fromNow(summary?.chatRenewCycle)}.`,
          theme: 'destructive',
        })
        // Refresh summary so chatUsage.exceeded flips and Send disables proactively.
        void fetchAccountSummary()
        return
      }
      errorToast('Could not send message', error as Error)
    })
    setValue('')
  }

  function handleStop() {
    stopMessage(slug)
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      handleSend()
    }
  }

  const placeholder = chatUsage.exceeded
    ? 'Monthly chat allowance reached'
    : sessionId === null
      ? 'Create a session to start chatting'
      : 'Message Daystrom'

  const canSend = !disabled && !sending && Boolean(value.trim()) && !overLimit

  if (trialChatUsedUp && !sending) {
    return (
      <div className="mx-auto w-full max-w-5xl px-6 pb-4">
        <TrialChatUsedUpNote />
      </div>
    )
  }

  return (
    <div className="mx-auto w-full max-w-5xl px-6 pb-4">
      <div
        className={cn(
          'flex items-end gap-2 rounded-lg border bg-card px-3 py-2',
          'transition-colors focus-within:border-foreground/40',
        )}
      >
        <AutoResizeTextarea
          value={value}
          onChange={(event) => setValue(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          maxLength={MAX_MESSAGE_LENGTH}
          className="min-h-0 flex-1 border-0 bg-transparent px-1 py-1.5 shadow-none focus-visible:ring-0"
          maxRows={8}
        />
        {sending ? (
          <Button
            size="sm"
            theme="destructive"
            isIcon
            onClick={handleStop}
            className="shrink-0"
            aria-label="Stop generating"
          >
            <Square className="h-3.5 w-3.5 fill-current" />
          </Button>
        ) : (
          <Button
            size="sm"
            isIcon
            onClick={handleSend}
            disabled={!canSend}
            aria-label="Send message"
            className="shrink-0"
          >
            <SendHorizonal className="h-4 w-4" />
          </Button>
        )}
      </div>
      {(showCounter || IS_ADMIN_MODE) && (
        <div className="mt-1.5 flex items-center justify-end gap-3 px-1 text-[11px] text-muted-foreground">
          {showCounter && (
            <span
              className={cn(
                'tabular-nums',
                overLimit && 'text-destructive',
                !overLimit && remaining <= 50 && 'text-foreground',
              )}
              aria-live="polite"
            >
              {charCount} / {MAX_MESSAGE_LENGTH}
            </span>
          )}
          {IS_ADMIN_MODE && (
            <button
              type="button"
              role="switch"
              aria-checked={diagnosticsEnabled}
              aria-label="Diagnostics"
              onClick={() => setDiagnosticsEnabled(!diagnosticsEnabled)}
              className={cn(
                'inline-flex cursor-pointer items-center gap-1 rounded transition-colors hover:text-foreground',
                diagnosticsEnabled && 'text-foreground',
              )}
            >
              <Activity className="h-3 w-3" />
              <span>Diagnostics</span>
            </button>
          )}
        </div>
      )}
    </div>
  )
}
